import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { Link } from "react-router-dom"
import ActionButton from "../../../common/button/actionbutton.component"
import ConfirmButton from "../../../common/button/confirmbutton.component"
import ConnectIntegrationButton from "../../../common/button/connectintegrationbutton.component"
import { WixFormComponentProps } from "../../../common/types/integration"
import { InventoryLocationSelectComponent } from "../../../inventory/components"
import CustomFieldService from "../../../state/custom-fields/CustomFieldService"
import { isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import IntegrationConfigurationService from "../../../state/integration/IntegrationConfigurationService"
import IntegrationConfigurationValue, { INTEGRATION_CONFIGURATION_KEYS } from "../../../state/integration/IntegrationConfigurationValue"
import { FormInputParametersDto, FormOptionsAttributesDto, FormOptionsPayload } from "../../../state/integration/form-options/types"
import { ExternalForm, SUPPORTED_CUSTOM_FIELD_OBJECTS, SUPPORTED_CUSTOM_FIELD_TYPES } from "../../../state/integration/wix/index"
import { renderConfigurationLabel } from "../../../utils/integration/pipedrive"
import {
    WIX_FORM_COMPONENT_MESSAGES,
    mapCustomFieldsToOptions,
    mapInputParametersToOptions,
    matchExternalFormToQMFields,
    validateFormFields,
    wasExternalFormChanged,
} from "../../../utils/integration/wix"
import ConfigurationValueComponent from "../ConfigurationValueComponent"
import FormMapping from "./FormMapping"
import FormOptionsComponent from "./FormOptionsComponent"

const WixFormComponent = (props: WixFormComponentProps & IntlProps) => {
    const {createQuoteOnFormSubmission} = props.configuration
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false)
    const [selectedForm, setSelectedForm] = useState<ExternalForm | undefined>(undefined)
    const [formOptions, setFormOptions] = useState<FormOptionsAttributesDto>({
        hash: "",
        userId: "",
        formTitle: "",
        formInputParameters: []
    })

    useEffect(() => {
        if(props.integrationConfigurationId !== "" && createQuoteOnFormSubmission){
            getConfigurationFormOptions()
        }
        if(createQuoteOnFormSubmission && selectedForm === undefined) {
            setIsSaveDisabled(true)
        }
    }, [createQuoteOnFormSubmission])

    useEffect(() => {
        props.toggleSaveButtonStatus(isSaveEnabled(props.configuration))
    }, [props.configuration])

    const getConfigurationFormOptions = async() => {
        try {
            const res = await IntegrationConfigurationService.getConfigurationFormOptions(INTEGRATION_KEY.WIX)
            if(!isError(res) && res?.attributes?.formInputParameters) {
                const { formTitle, formInputParameters } = res.attributes

                if(formTitle && formTitle.trim() !== "") {
                    setFormOptions(res.attributes)
                } else {
                    handleFormSetCallback(res.attributes)
                }

                getCustomFields(formInputParameters)

                if(selectedForm?.fields) {
                    matchExternalFormToQMFields(formInputParameters, selectedForm.fields)
                }
            }
        } catch (error) {
            console.error("Error fetching configuration form options:", error)
        }
    }

    const getCustomFields = async(formInputParameters: FormInputParametersDto[]) => {
        try {
            const res = await CustomFieldService.getAvailableByObjectAndTypes(
                SUPPORTED_CUSTOM_FIELD_OBJECTS,
                SUPPORTED_CUSTOM_FIELD_TYPES
            )
            if(!isError(res) && res) {
                if(res.length == 0) {
                    return
                }
                let cfMapping = mapCustomFieldsToOptions(res)
                if(formInputParameters.length > 0) {
                    // filter custom fields already mapped and saved
                    cfMapping = cfMapping.filter(cf => !formInputParameters.some(fo => fo.label === cf.label))
                }
                
                setFormOptions((prevOptions) => ({
                    ...prevOptions,
                    formInputParameters: [
                        ...prevOptions.formInputParameters,
                        ...cfMapping
                    ]
                }))
            }
        } catch (error) {
            console.error("Error fetching custom fields for quotes:", error)
        }
    }

    const handleOnSaveClick = () => {
        const formTitle = selectedForm?.displayName || ""
        const { hash, userId, formInputParameters } = formOptions
        const formOptionsPayload: FormOptionsPayload = {
            hash: hash,
            formOptions: {
                userId: userId,
                formTitle: formTitle,
                inputFormOptions: mapInputParametersToOptions(formInputParameters),
            }
        }
        props.submitIntegration(formOptionsPayload)
    }

    const getFormFooter = () => {
        return <Row>
            <Col md={6}>
                <ConfirmButton
                    onClick={props.deleteIntegration}
                    className="btn btn-danger">
                    {props.intl.formatMessage(WIX_FORM_COMPONENT_MESSAGES.deleteBtn)}
                </ConfirmButton>
            </Col>
            <Col md={6} className="text-align-right">
                <Link to="/integrations/listing" className="btn btn-light mr-2">
                    {props.intl.formatMessage(WIX_FORM_COMPONENT_MESSAGES.cancelBtn)}
                </Link>
                { !props.authConnect && <ActionButton
                    disabled={isSaveDisabled}
                    onClick={() => handleOnSaveClick()}
                    type="submit"
                    className="btn btn-primary">
                    {props.intl.formatMessage(WIX_FORM_COMPONENT_MESSAGES.saveBtn)}
                </ActionButton>
                }
            </Col>
        </Row>
    }

    //  ************  FormOptionsComponent related functions  ************  //
    const handleExternalFormChange = (selectedExtForm: ExternalForm) => {  
        setSelectedForm((prevSelectedForm) => {
            // Check if the previous form is defined and the displayName is not empty
            const externalFormChanged = wasExternalFormChanged(prevSelectedForm, selectedExtForm)

            if(externalFormChanged) {
                const mapping  = resetMapping()
                setFormOptions((prevOptions) => ({
                    ...prevOptions,
                    formTitle: selectedExtForm.displayName,
                    formInputParameters: mapping
                }))
            } else {
                setFormOptions((prevOptions) => ({
                    ...prevOptions,
                    formTitle: selectedExtForm.displayName,
                }))
            }
            
            return selectedExtForm
        })

        setIsSaveDisabled(!validateFormFields(formOptions.formInputParameters))
    }

    const resetMapping = () => {
        const newMapping: FormInputParametersDto[] = formOptions.formInputParameters.map(field => ({
            ...field,
            key: ""
        }))
        return newMapping
    }

    const handleOnConfigurationChange = () => {
        props.onConfigurationChange({
            ...props.configuration,
            ...{ createQuoteOnFormSubmission: !createQuoteOnFormSubmission },
        })
    }
    
    //  ************  FormMapping related functions  ************  //
    const handleFormMappingChange = (mapping: FormInputParametersDto[]) => {
        setFormOptions((prevOptions) => ({
            ...prevOptions,
            formInputParameters: mapping
        }))
        setIsSaveDisabled(!validateFormFields(mapping))
    }

    const handleFormSetCallback = (form: FormOptionsAttributesDto) => {
        setFormOptions((prevOptions) => ({
            ...prevOptions,
            hash: form.hash,
            userId: form.userId,
            formInputParameters: form.formInputParameters
        }))
    }

    if (!props.integrationConfigurationId) {
        const url = AppContext.getOptions().apiUrl + `/openid/connect/${INTEGRATION_KEY.WIX}/` +
            AppContext.getUserLoggedIn().id

        return <Col className="ui-module">
            <Row className="form-group">
                <label className="col-3 col-form-label text-align-right">
                    <FormattedMessage
                        id="integration.form.edit.connect"
                        defaultMessage="Connect"
                    />
                </label>
                <Col sm={9}>
                    <ConnectIntegrationButton integrationKey={INTEGRATION_KEY.WIX} url={url} logoHeight={12} logoWidth={31} />
                </Col>
            </Row>
        </Col>
    }

    return <>
        <Col md={12} className="ui-module">
            <Row className="form-group">
                <label className="col-3 col-form-label text-align-right" htmlFor="accountId">
                    <FormattedMessage
                        id="integration.form.edit.wix.account_id"
                        defaultMessage="Account ID"
                    />
                </label>
                <Col sm={6}>
                    <input
                        name="accountId"
                        value={props.configuration.accountId}
                        disabled={true}
                        id="accountId"
                        type="text"
                        className="form-control"
                    />
                </Col>
            </Row>
            <Row>
                <label className="col-3 col-form-label text-align-right" htmlFor="inventoryLocationId">
                    <FormattedMessage
                        id="integration.form.edit.wix.locations_label"
                        defaultMessage="Default inventory location"
                    />
                </label>
    
                <Col sm={6}>
                    <InventoryLocationSelectComponent
                        className="select-component"
                        selectedValue={props.configuration.defaultInventoryLocationId}
                        onChangeCallback={selected => {
                            props.configuration[INTEGRATION_CONFIGURATION_KEYS.defaultInventoryLocationId] = selected.value
                            props.onConfigurationChange(props.configuration)
                            props.toggleSaveButtonStatus(isSaveEnabled(props.configuration))
                        }}
                    />
                </Col>
            
            </Row> 

            {props.configurationValues.map(v => {
                const cv = new IntegrationConfigurationValue(v)
                return (
                    <ConfigurationValueComponent
                        key={cv.getId()}
                        configurationValue={cv}
                        value={props.configuration[cv.getKey()]}
                        onChange={value => {
                            props.configuration[cv.getKey()] = value
                            props.onConfigurationChange(props.configuration)
                            props.toggleSaveButtonStatus(isSaveEnabled(props.configuration))
                        }}
                        renderConfigurationValueLabel={renderConfigurationLabel}
                        intl={props.intl}
                    />
                )
            })}

            <FormOptionsComponent
                createQuoteOnFormSubmission={createQuoteOnFormSubmission}
                mappedForm={formOptions.formTitle}
                onToggleChange={handleOnConfigurationChange}
                selectedForm={selectedForm}
                onExternalFormSetCallback={handleExternalFormChange}
            />

            {createQuoteOnFormSubmission && selectedForm && <FormMapping 
                selectedExternalForm={selectedForm}
                inputParameters={formOptions.formInputParameters}
                onMappingChangeCallback={handleFormMappingChange}
                onFormSetCallback={handleFormSetCallback}
            />}
        </Col>
        <Col md={12} className="ui-module">
            {getFormFooter()}
        </Col>
    </>
}

const isSaveEnabled = (configuration: object): boolean => {
    return !(configuration[INTEGRATION_CONFIGURATION_KEYS.defaultInventoryLocationId] === "" )
}

export default injectIntl(WixFormComponent)


// WEBPACK FOOTER //
// ./src/integration/form/wix/WixFormComponent.tsx